import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { CallbackComponent } from './core/auth/callback.component';
import { LoginComponent } from './core/auth/login.component';
import { featureGuard } from './core/features/feature.guard';
import { landingPath } from './core/navigation/landing';
import { FEATURES } from './core/features/feature.service';
import { PermissionService } from './core/permissions/permission.service';
import { superAdminGuard } from './core/permissions/super-admin.guard';
import { clientPromptGuard } from './core/session/client-prompt.guard';
import { MainLayoutComponent } from './shared/layout/main-layout.component';

/**
 * Contact.Web's URL contract is preserved so existing cross-app deep links keep resolving:
 *   /people, /people/new, /people/:id, /people/:id/edit, /people/manage/:id
 * plus the ?returnUrl=&embedded=true query pair.
 *
 * Every signed-in page renders INSIDE MainLayoutComponent (the sidebar shell), so the layout is not
 * something each page has to remember to include. /callback stays outside it: the user is not
 * authenticated yet when they land there, so there is no session to build a menu from.
 */
export const routes: Routes = [
  // The OIDC redirect target. Must NOT be guarded — the user is not yet authenticated when they land.
  { path: 'callback', component: CallbackComponent },

  // The SPA login page. AuthServer redirects the core-web-spa authorize challenge here for
  // unauthenticated users (OnRedirectToLogin), with ?returnUrl=<the authorize URL to resume>. Also
  // unguarded — the guard would call authorize(), which is exactly what bounced the user here.
  { path: 'login', component: LoginComponent },

  // "Which client do you want to start in?" — where /callback sends a multi-client user before they
  // reach the app. authGuard, because you must be signed in to have clients at all; deliberately NO
  // featureGuard, and it must never become the target of landingPath(): both would make the first
  // screen after sign-in a redirect loop. Outside MainLayoutComponent because the sidebar is built
  // from the active client's features, which is the very thing not settled yet. Lazy, like every
  // other screen off the main path — the initial bundle has a hard CI ceiling.
  {
    path: 'select-client',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/select-client-page.component').then(
        (m) => m.SelectClientPageComponent,
      ),
  },

  // "Forgot your password?" — unguarded for the same reason as /login (the user is locked out by
  // definition), and OUTSIDE MainLayoutComponent so it renders without a session to build a menu
  // from. Lazy: it is reached by a handful of people a week and must not sit in the initial bundle.
  // Self-registration. Unguarded and outside MainLayoutComponent for the same reason as /login and
  // /forgot-password — there is no session yet, so a guard would bounce the visitor into the sign-in
  // loop this page exists to start. Lazy: almost nobody registers, and it must not sit in the initial
  // bundle, which is already close to its CI ceiling.
  {
    path: 'register',
    loadComponent: () =>
      import('./features/account/register-page.component').then((m) => m.RegisterPageComponent),
  },

  // Where the emailed confirm link lands. Unguarded: confirming is what makes signing in possible, so
  // requiring a session to reach it would be a loop with no way out.
  // Where an administrator's "set your password" link lands. Unguarded: the account has NO password
  // until this page succeeds, so requiring a session to reach it would be a loop with no way out.
  {
    path: 'set-password',
    loadComponent: () =>
      import('./features/account/set-password-page.component').then(
        (m) => m.SetPasswordPageComponent,
      ),
  },

  // Where an emailed signing link lands. Unguarded and outside MainLayoutComponent for the strongest
  // version of the reason /set-password is: a customer signing a contract has no account and never
  // will, so there is no session to guard or to build a menu from.
  //
  // Lazy, and it must stay that way. It pulls in pdfjs-dist, and it is the one page in the product
  // reached by people on mobile data who have never heard of us — the initial bundle is already
  // within ~320 kB of the hard `ng build` ceiling.
  {
    path: 'sign/:token',
    loadComponent: () =>
      import('./features/signing/sign-page.component').then((m) => m.SignPageComponent),
  },

  {
    path: 'confirm-email',
    loadComponent: () =>
      import('./features/account/confirm-email-page.component').then(
        (m) => m.ConfirmEmailPageComponent,
      ),
  },

  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/account/forgot-password-page.component').then(
        (m) => m.ForgotPasswordPageComponent,
      ),
  },

  {
    path: '',
    component: MainLayoutComponent,
    // clientPromptGuard asks which client to start in, once per browser session. It is HERE rather
    // than in CallbackComponent because a restored session never passes through /callback — see the
    // guard's own notes. Ordered after authGuard: there is nothing to ask an anonymous visitor.
    canActivate: [authGuard, clientPromptGuard],
    children: [
      // Computed, because where a login belongs depends on what the client bought. See landingPath.
      { path: '', pathMatch: 'full', redirectTo: () => landingPath() },

      {
        path: 'people',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadChildren: () => import('./features/people/people.routes').then((m) => m.peopleRoutes),
      },

      {
        path: 'jobs',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadChildren: () => import('./features/jobs/jobs.routes').then((m) => m.jobsRoutes),
      },

      {
        path: 'leads',
        canActivate: [featureGuard(FEATURES.coreApplication, FEATURES.leads)],
        loadComponent: () =>
          import('./features/leads/leads-list.component').then((m) => m.LeadsListComponent),
      },

      // One lead, and the sales run that starts from it: the contract that gates the sale, and
      // turning the lead into a customer once it is signed. Its own page rather than more of the
      // list's edit modal — three actions, each with its own permission, do not belong in a dialog.
      {
        path: 'leads/:id',
        canActivate: [featureGuard(FEATURES.coreApplication, FEATURES.leads)],
        loadComponent: () =>
          import('./features/leads/lead-detail-page.component').then(
            (m) => m.LeadDetailPageComponent,
          ),
      },

      {
        path: 'boards',
        canActivate: [featureGuard(FEATURES.taskBoards)],
        loadChildren: () => import('./features/boards/boards.routes').then((m) => m.boardsRoutes),
      },

      {
        path: 'big-board',
        canActivate: [featureGuard(FEATURES.bigBoard)],
        loadChildren: () =>
          import('./features/big-board/big-board.routes').then((m) => m.bigBoardRoutes),
      },

      // The definitions behind the board: named calendars and repeating events. Same feature gate —
      // there is no point defining a repeating event for somebody with no calendar to see it on.
      {
        path: 'calendar',
        canActivate: [featureGuard(FEATURES.bigBoard)],
        loadChildren: () =>
          import('./features/calendar/calendar.routes').then((m) => m.calendarRoutes),
      },

      // Flat paths, matching Contact.Web's URLs exactly, so its links keep resolving.
      {
        path: 'employees',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/employees/employees-page.component').then(
            (m) => m.EmployeesPageComponent,
          ),
      },
      {
        path: 'customers',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/customers/customers-page.component').then(
            (m) => m.CustomersPageComponent,
          ),
      },
      {
        path: 'sub-contractors',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/subcontractors/subcontractors-page.component').then(
            (m) => m.SubContractorsPageComponent,
          ),
      },
      {
        path: 'organizations',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/organizations/organizations-page.component').then(
            (m) => m.OrganizationsPageComponent,
          ),
      },

      {
        path: 'branches',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadChildren: () =>
          import('./features/branches/branches.routes').then((m) => m.branchesRoutes),
      },

      // In-house signing, sender side. Gated on ESignature, which is OFF by default and must stay
      // off for anything contractual until the Certificate of Completion exists — a signed PDF
      // without one looks finished to whoever finds it.
      {
        path: 'esignature',
        canActivate: [featureGuard(FEATURES.eSignature)],
        loadChildren: () =>
          import('./features/esignature/esignature.routes').then((m) => m.esignatureRoutes),
      },

      {
        path: 'documents',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/documents/documents-page.component').then(
            (m) => m.DocumentsPageComponent,
          ),
      },

      // The stored mail — every message the active client sent or received (api/emails).
      {
        path: 'inbox',
        canActivate: [featureGuard(FEATURES.inbox)],
        loadComponent: () =>
          import('./features/inbox/inbox-page.component').then((m) => m.InboxPageComponent),
      },

      // Broadcasts — one message to many people, now or scheduled. Gated on TEXT messaging only:
      // a voice campaign additionally needs VoiceMessaging, but that is checked per campaign (the
      // channel is chosen inside the composer), and a client with voice alone must still reach the
      // list to see its own campaigns.
      {
        path: 'messaging',
        canActivate: [featureGuard(FEATURES.textMessaging)],
        loadChildren: () =>
          import('./features/messaging/messaging.routes').then((m) => m.messagingRoutes),
      },

      // The standalone contact-value tables: /phones, /email-addresses, /addresses.
      {
        path: '',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadChildren: () =>
          import('./features/communication/communication.routes').then(
            (m) => m.communicationRoutes,
          ),
      },

      // Batch Billing lived under Integrations until it became a section of its own. Redirected
      // rather than dropped: the old path was handed out with setup codes.
      {
        path: 'admin/integrations/batch-billing/installations',
        redirectTo: 'batch-billing/installations',
      },

      // The user's own menu arrangement and startup page. No guard beyond signing in: it edits a
      // preference over what the menu ALREADY shows this person, so there is nothing here a feature
      // or a permission could sensibly gate. Lazy, like every other rarely-opened settings screen —
      // the initial bundle has a hard CI ceiling and has been over its warning line for a while.
      {
        path: 'account/menu',
        loadComponent: () =>
          import('./features/account/menu-settings-page.component').then(
            (m) => m.MenuSettingsPageComponent,
          ),
      },

      // The user's own notification settings — which channels they hear about each event on. Same
      // reasoning as the menu page above and no guard for the same reason: it records a preference
      // about what reaches this one person, and it grants nothing. Lazy, like every other
      // rarely-opened settings screen.
      {
        path: 'account/notifications',
        loadComponent: () =>
          import('./features/account/notification-settings-page.component').then(
            (m) => m.NotificationSettingsPageComponent,
          ),
      },

      // Help → Send feedback. No guard: any signed-in user may tell us something, and the server
      // decides where it lands (ClientFeedbackService). A page rather than a dialog so the
      // BatchBilling desktop app's Help menu can link straight to it.
      {
        path: 'feedback',
        loadComponent: () =>
          import('./features/feedback/feedback-page.component').then(
            (m) => m.FeedbackPageComponent,
          ),
      },

      // Account self-service (served by AuthServer's headless API, not its Razor pages).
      {
        path: 'account/external-logins',
        loadComponent: () =>
          import('./features/account/external-logins-page.component').then(
            (m) => m.ExternalLoginsPageComponent,
          ),
      },

      // Admin.Web's pages, as they move over.
      // The two tenant-boundary screens. Super Admin only, guarded rather than merely hidden: the
      // API refuses them outright ([SuperAdminOnly]), so without the guard a typed URL renders a
      // page whose every call 403s.
      {
        path: 'admin/clients',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/clients-page.component').then((m) => m.ClientsPageComponent),
      },
      {
        // The vendor's billing screen: it charges cards and opens other people's Stripe portals.
        // Guarded, not merely hidden — the API is [SuperAdminOnly], so a typed URL without this
        // renders a page whose every call 403s.
        path: 'admin/billing',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/platform-billing-page.component').then(
            (m) => m.PlatformBillingPageComponent,
          ),
      },
      {
        // What this tier still needs after go-live, and what the active client still needs (card
        // 189). A permission rather than superAdminGuard: a client administrator may see their own
        // client's cut-down report. The page hides the environment tab from them, and its API stays
        // [SuperAdminOnly]; Super Admin passes the permission check.
        path: 'admin/setup',
        canActivate: [
          () =>
            inject(PermissionService).has('ClientSetup Viewer') ||
            inject(Router).createUrlTree(['/people']),
        ],
        loadComponent: () =>
          import('./features/admin/platform-setup-page.component').then(
            (m) => m.PlatformSetupPageComponent,
          ),
      },
      {
        // ComputAll's AWS bill by product, client and environment (card 186). Guarded for the same
        // reason as its neighbours: the API is [SuperAdminOnly].
        path: 'admin/costs',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/costs/costs-dashboard-page.component').then(
            (m) => m.CostsDashboardPageComponent,
          ),
      },
      {
        // One client far above its own normal, and pausing them (cost & abuse watch). The API is
        // [SuperAdminOnly], so the route is guarded the same way.
        path: 'admin/cost-watch',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/costs/cost-watch-page.component').then(
            (m) => m.CostWatchPageComponent,
          ),
      },
      {
        // Dev and test on/off outside their weekday schedule (2026-10-24). Guarded for the same reason
        // as its neighbours: the API is [SuperAdminOnly]. It works on production only.
        path: 'admin/nonprod-power',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/nonprod-power/nonprod-power-page.component').then(
            (m) => m.NonProdPowerPageComponent,
          ),
      },
      {
        path: 'admin/costs/settings',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/costs/costs-settings-page.component').then(
            (m) => m.CostsSettingsPageComponent,
          ),
      },
      {
        path: 'admin/client-memberships',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/client-memberships-page.component').then(
            (m) => m.ClientMembershipsPageComponent,
          ),
      },
      {
        // Deliberately NOT superAdminGuard, unlike client-memberships beside it: this is the
        // delegated surface, reachable by whoever holds the SubclientAccess key. The server is the
        // real gate — it refuses anything outside the caller's own subtree.
        path: 'admin/subclient-access',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/subclient-access-page.component').then(
            (m) => m.SubclientAccessPageComponent,
          ),
      },
      {
        path: 'admin/users',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/users-page.component').then((m) => m.UsersPageComponent),
      },
      {
        path: 'admin/roles',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/roles-page.component').then((m) => m.RolesPageComponent),
      },
      {
        path: 'admin/role-permissions',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/role-permissions-page.component').then(
            (m) => m.RolePermissionsPageComponent,
          ),
      },
      {
        path: 'admin/menus',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/menus-page.component').then((m) => m.MenusPageComponent),
      },
      {
        path: 'admin/menu-items',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/menu-items-page.component').then(
            (m) => m.MenuItemsPageComponent,
          ),
      },
      {
        path: 'admin/menu-item-permissions',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/menu-item-permissions-page.component').then(
            (m) => m.MenuItemPermissionsPageComponent,
          ),
      },
      {
        path: 'admin/activities',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/activities-page.component').then(
            (m) => m.ActivitiesPageComponent,
          ),
      },
      {
        // Automations for every subject that is NOT a job — leads today. Job automations stay in
        // admin/activities, on the activity they hang off, because that is where they mean
        // something.
        path: 'admin/automations',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/workflow-rules-page.component').then(
            (m) => m.WorkflowRulesPageComponent,
          ),
      },
      {
        path: 'admin/activity-templates',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/activity-templates-page.component').then(
            (m) => m.ActivityTemplatesPageComponent,
          ),
      },
      {
        path: 'admin/document-name-templates',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/document-name-templates-page.component').then(
            (m) => m.DocumentNameTemplatesPageComponent,
          ),
      },
      {
        path: 'admin/integrations',
        loadComponent: () =>
          import('./features/admin/integrations-page.component').then(
            (m) => m.IntegrationsPageComponent,
          ),
      },
      {
        // Lazy like its siblings: only a client that licenses Accounting AND wants a LIVE bank feed
        // ever opens this — a statement-file feed needs no configuration at all — and the initial
        // budget has been outgrown once already.
        path: 'admin/integrations/bank-feeds',
        canActivate: [featureGuard(FEATURES.accounting)],
        loadComponent: () =>
          import('./features/admin/bank-feed-providers-page.component').then(
            (m) => m.BankFeedProvidersPageComponent,
          ),
      },
      {
        // Lazy, like its sibling: a settings screen most people never open must not be in the
        // initial bundle, which has a hard budget this repo has outgrown once already.
        path: 'admin/integrations/quickbooks',
        loadComponent: () =>
          import('./features/admin/qbsync-settings-page.component').then(
            (m) => m.QbSyncSettingsPageComponent,
          ),
      },
      {
        // Lazy for the same reason as its siblings, and more so: most clients have no QuickBooks
        // Desktop installation at all.
        path: 'admin/integrations/quickbooks/installations',
        loadComponent: () =>
          import('./features/admin/qbsync-installs-page.component').then(
            (m) => m.QbSyncInstallsPageComponent,
          ),
      },
      {
        // One installation: its sync jobs, set up here and applied by QBSync on its next check-in.
        path: 'admin/integrations/quickbooks/installations/:id',
        loadComponent: () =>
          import('./features/admin/qbsync-installation-page.component').then(
            (m) => m.QbSyncInstallationPageComponent,
          ),
      },
      {
        // Batch Billing shipped linking at /batch-billing/licence, and an installed copy builds that
        // address from the site it enrolled against — so the British spelling has to keep resolving
        // for as long as those copies exist. A forwarding note, not a second screen.
        path: 'batch-billing/licence',
        redirectTo: 'batch-billing/license',
      },
      {
        // The customer's own license: what they hold, until when, and what they paid. Same feature
        // gate as the installations screen beside it.
        path: 'batch-billing/license',
        canActivate: [featureGuard(FEATURES.batchBilling)],
        loadComponent: () =>
          import('./features/admin/batchbilling-license-page.component').then(
            (m) => m.BatchBillingLicensePageComponent,
          ),
      },
      {
        // Lazy, like every other installed-application screen: only providers who license Batch
        // Billing ever open this, and the initial budget has been outgrown once already.
        path: 'batch-billing/installations',
        // Entitlement, not permission: a Super Admin signed into a client that does not license
        // Batch Billing must not reach this either, and no permission arrangement can express that.
        canActivate: [featureGuard(FEATURES.batchBilling)],
        loadComponent: () =>
          import('./features/admin/batchbilling-installs-page.component').then(
            (m) => m.BatchBillingInstallsPageComponent,
          ),
      },
      {
        path: 'admin/notifications',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/notifications-page.component').then(
            (m) => m.NotificationsPageComponent,
          ),
      },
      {
        path: 'admin/email-templates',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/email-templates-page.component').then(
            (m) => m.EmailTemplatesPageComponent,
          ),
      },
      {
        // The report configurator. Declaring a report and what it asks for; the stored procedure
        // behind it is written by a developer in SQL Server.
        path: 'admin/reports',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/report-definitions-page.component').then(
            (m) => m.ReportDefinitionsPageComponent,
          ),
      },
      {
        // Full-page editor. Declared AFTER the list so the literal path wins, and `:id` accepts
        // `new` — the component treats anything unparseable as "create".
        path: 'admin/email-templates/:id',
        canActivate: [featureGuard(FEATURES.clientAdministration)],
        loadComponent: () =>
          import('./features/admin/email-template-edit-page.component').then(
            (m) => m.EmailTemplateEditPageComponent,
          ),
      },
      {
        path: 'admin/email-providers',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/admin/email-providers-page.component').then(
            (m) => m.EmailProvidersPageComponent,
          ),
      },
      {
        path: 'admin/sms-providers',
        canActivate: [featureGuard(FEATURES.textMessaging)],
        loadComponent: () =>
          import('./features/admin/sms-providers-page.component').then(
            (m) => m.SmsProvidersPageComponent,
          ),
      },
      {
        path: 'admin/esignature-providers',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/admin/esignature-providers-page.component').then(
            (m) => m.ESignatureProvidersPageComponent,
          ),
      },
      {
        path: 'admin/crm-providers',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/admin/crm-providers-page.component').then(
            (m) => m.CrmProvidersPageComponent,
          ),
      },
      {
        path: 'admin/login-providers',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/admin/login-providers-page.component').then(
            (m) => m.LoginProvidersPageComponent,
          ),
      },
      {
        path: 'admin/storage-providers',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/admin/storage-providers-page.component').then(
            (m) => m.StorageProvidersPageComponent,
          ),
      },
      {
        path: 'admin/document-upload-settings',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/admin/document-upload-settings-page.component').then(
            (m) => m.DocumentUploadSettingsPageComponent,
          ),
      },
      {
        path: 'admin/client-domains',
        canActivate: [featureGuard(FEATURES.customDomains)],
        loadComponent: () =>
          import('./features/admin/client-domains-page.component').then(
            (m) => m.ClientDomainsPageComponent,
          ),
      },

      // Which clients have which features. Deliberately NOT feature-gated — this is the screen that
      // manages entitlement, and gating it on entitlement is how an installation locks itself out of
      // its own administration.
      // This tier's secrets. superAdminGuard as well as the server's [SuperAdminOnly], because a
      // route anybody can reach renders a list of every credential the platform expects to hold --
      // which is a map of what to go after, even without the values.
      {
        path: 'admin/secrets',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./features/admin/platform-secrets-page.component').then(
            (m) => m.PlatformSecretsPageComponent,
          ),
      },

      {
        path: 'admin/features',
        loadComponent: () =>
          import('./features/admin/features-page.component').then((m) => m.FeaturesPageComponent),
      },

      // The lookup tables (phone types, email types, …) — one component, one route each.
      {
        path: 'reports',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/reports/reports-page.component').then((m) => m.ReportsPageComponent),
      },
      {
        // Declared after the index so the literal path wins.
        path: 'reports/:key',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadComponent: () =>
          import('./features/reports/report-runner-page.component').then(
            (m) => m.ReportRunnerPageComponent,
          ),
      },
      {
        path: 'reference',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadChildren: () =>
          import('./features/reference/reference.routes').then((m) => m.referenceRoutes),
      },

      // Accounting — accounts payable: business expenses keyed as they happen, marked
      // reimbursable or paid from a business account/card.
      {
        path: 'accounting',
        canActivate: [featureGuard(FEATURES.accounting)],
        loadChildren: () =>
          import('./features/accounting/accounting.routes').then((m) => m.accountingRoutes),
      },

      // Plaid's OAuth return — /web/plaid/oauth, registered EXACTLY under Plaid's Allowed redirect
      // URIs for every tier and sent by the server on every link token. Do not move or rename it.
      {
        path: 'plaid/oauth',
        canActivate: [featureGuard(FEATURES.accounting)],
        loadComponent: () =>
          import('./features/accounting/plaid-oauth-page.component').then(
            (m) => m.PlaidOAuthPageComponent,
          ),
      },

      // Warehouse storage — the register of what a client holds for its customers, and (later) the
      // rules that price it. Its own feature: a client that does not store other people's property
      // has no use for any of it, and a facilities menu in a roofing app is precisely the failure
      // the entitlement ledger exists to prevent.
      {
        path: 'warehouse',
        canActivate: [featureGuard(FEATURES.warehousing)],
        loadChildren: () =>
          import('./features/warehouse/warehouse.routes').then((m) => m.warehouseRoutes),
      },

      // General ledger — the chart of accounts, and the postings coded against it. Beside
      // Accounting rather than inside it, and its own feature for the same reason Invoicing is:
      // keying expenses and keeping a general ledger are separate claims, and either can be the
      // one a client bought.
      {
        path: 'general-ledger',
        canActivate: [featureGuard(FEATURES.generalLedger)],
        loadChildren: () =>
          import('./features/general-ledger/general-ledger.routes').then(
            (m) => m.generalLedgerRoutes,
          ),
      },

      // Invoicing — what this client's own customers owe THEM. The other direction of travel from
      // Accounting above, and its own feature for that reason: a client can key expenses here and
      // invoice from QuickBooks, or the reverse.
      {
        path: 'invoices',
        canActivate: [featureGuard(FEATURES.invoicing)],
        loadChildren: () =>
          import('./features/invoicing/invoicing.routes').then((m) => m.invoicingRoutes),
      },

      // What QBSync replicated from a client's QuickBooks Desktop company, read-only. No feature
      // guard: it is not Accounting or Invoicing (FTA has neither and is the first client with the
      // data), and a client with no installation gets an empty page that says how to get one.
      {
        path: 'quickbooks',
        loadComponent: () =>
          import('./features/quickbooks/quickbooks-customers-page.component').then(
            (m) => m.QuickBooksCustomersPageComponent,
          ),
      },
      {
        path: 'quickbooks/customers/:listId',
        loadComponent: () =>
          import('./features/quickbooks/quickbooks-customer-page.component').then(
            (m) => m.QuickBooksCustomerPageComponent,
          ),
      },

      // Subscriptions a client sells to their OWN customers. Its own feature rather than part of
      // invoicing: most clients who raise invoices will never sell one, and where an arrangement
      // names a client it decides whether that client's software keeps working — which is not a
      // power anybody should acquire by having invoicing switched on.
      {
        path: 'customer-subscriptions',
        canActivate: [featureGuard(FEATURES.customerSubscriptions)],
        loadComponent: () =>
          import('./features/subscriptions/customer-subscriptions-page.component').then(
            (m) => m.CustomerSubscriptionsPageComponent,
          ),
      },

      // A client's own price list — what it sells to its customers, and what paying for each thing
      // switches on. Its own feature rather than part of customer subscriptions: a tenant may sell
      // arrangements without ever wanting a catalog behind them, typing each invoice line by hand.
      {
        path: 'products',
        canActivate: [featureGuard(FEATURES.productOfferings)],
        loadComponent: () =>
          import('./features/products/product-offerings-page.component').then(
            (m) => m.ProductOfferingsPageComponent,
          ),
      },

      // Fuel tax (IFTA) — the jurisdictions a quarterly return reports on and their
      // effective-dated rates. Replaces the Access application FTA files returns from.
      {
        path: 'fuel-tax',
        canActivate: [featureGuard(FEATURES.fuelTax)],
        loadChildren: () =>
          import('./features/fuel-tax/fuel-tax.routes').then((m) => m.fuelTaxRoutes),
      },

      // The public car-show site's authoring screens — shows, recurring series, the curated car
      // gallery, its four lookups, and where the site gets published to.
      //
      // featureGuard on the loadChildren route, so a client without Car Shows never even downloads
      // the chunk. Hiding the menu item is not enough on its own: a bookmark or a pasted link lands
      // here directly, and without the guard the user gets a fully-rendered screen whose every call
      // comes back 404 — which reads as a broken app rather than a feature they do not have.
      {
        path: 'car-shows',
        canActivate: [featureGuard(FEATURES.carShows)],
        loadChildren: () =>
          import('./features/car-shows/car-shows.routes').then((m) => m.carShowRoutes),
      },

      // Weather watch: the alert board people work every morning, and the rules behind it.
      // Gated on WeatherWatch — the feature reads a third party's calendar on a schedule and copies
      // customer contact details in, so it is off unless a client has been granted it.
      {
        path: 'weather',
        canActivate: [featureGuard(FEATURES.weatherWatch)],
        loadChildren: () =>
          import('./features/weather/weather.routes').then((m) => m.weatherRoutes),
      },

      // The sites themselves, their authored pages and the publisher. Gated on PublicSite rather
      // than CarShows: three of the four sites this exists for have no calendar on them at all.
      {
        path: 'public-site',
        canActivate: [featureGuard(FEATURES.publicSite)],
        loadChildren: () =>
          import('./features/public-sites/public-site.routes').then((m) => m.publicSiteRoutes),
      },

      // Departments and their groups — bespoke screens (a department has a colour and a group, a
      // group has production / QB-sync flags), so they do not ride the generic reference screen.
      {
        path: 'departments',
        canActivate: [featureGuard(FEATURES.coreApplication)],
        loadChildren: () =>
          import('./features/departments/departments.routes').then((m) => m.departmentsRoutes),
      },

      // Cullinan Rigging work orders — the port of their Access application. Its own area rather
      // than a job screen: a rigging haulage ticket is not a Core Job (which is claims/roofing
      // shaped and requires a Customer), so it has its own entity and its own screen.
      {
        path: 'rigging',
        canActivate: [featureGuard(FEATURES.rigging)],
        loadChildren: () =>
          import('./features/rigging/rigging.routes').then((m) => m.riggingRoutes),
      },

      // Citizen developer: designated users describe a change to an agent, watch it become a
      // branch + PR, review the preview, and approve. Doubly gated like everything else here —
      // the guard is the client's entitlement; the per-person permission gates the API.
      {
        path: 'citizen-developer',
        canActivate: [featureGuard(FEATURES.citizenDeveloper)],
        loadChildren: () =>
          import('./features/citizen-developer/citizen-developer.routes').then(
            (m) => m.citizenDeveloperRoutes,
          ),
      },

      // What's new: the product changelog. Deliberately NO featureGuard, unlike almost everything
      // else here — a feature gates on what the active client has LICENSED, and a release of the
      // product happened whoever is asking. The server agrees: the endpoint carries a permission
      // and no [RequiresFeature], for the same reason. Lazy, like every screen off the main path:
      // the initial bundle has a hard CI ceiling.
      {
        path: 'releases',
        loadChildren: () =>
          import('./features/releases/releases.routes').then((m) => m.releasesRoutes),
      },
    ],
  },

  // Same for anything unrecognised: a stale bookmark from a client that has since been switched to
  // one product must not resolve to a screen that client no longer has.
  { path: '**', redirectTo: () => landingPath() },
];
